import React, { useState, useEffect } from 'react'
import {
  type SupportTicket,
  type SupportAgent
} from '../../lib/supportSystem'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
import SupportAgentManager from './SupportAgentManager'
import SupportInbox from './SupportInbox'
import QuickChatAdmin from './QuickChatAdmin'
import ConsultationAdmin from './ConsultationAdmin'
import CannedResponseManager from './CannedResponseManager'
import TicketAutomations from './TicketAutomations'
import SLAPolicyManager from './SLAPolicyManager'
import SupportAnalytics from './SupportAnalytics'
import { getSessionHeaders } from '../../lib/serverSession';

interface ComprehensiveSupportDashboardProps {
  currentUser?: any
}

const ComprehensiveSupportDashboard: React.FC<ComprehensiveSupportDashboardProps> = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('support-inbox')
  const [loading, setLoading] = useState(true)
  const [_stats, setStats] = useState({
    totalTickets: 0,
    openTickets: 0,
    inProgressTickets: 0,
    resolvedTickets: 0,
    activeAgents: 0,
    availableAgents: 0,
    activeChatSessions: 0,
    avgResponseTime: 0
  })
  const [_recentTickets, setRecentTickets] = useState<SupportTicket[]>([])
  const [currentAgent, setCurrentAgent] = useState<SupportAgent | null>(null)
  const [tabNotifications, setTabNotifications] = useState<Record<string, number>>({})

  useEffect(() => {
    loadDashboardData()
    loadTabNotifications()
    const notifInterval = setInterval(loadTabNotifications, 30000)
    return () => clearInterval(notifInterval)
  }, [currentUser])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      await Promise.all([
        loadStats(),
        loadRecentTickets(),
        loadCurrentAgent()
      ])
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadTabNotifications = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_tab_notifications' })
      })
      const result = await response.json()
      if (response.ok && result.data) {
        setTabNotifications({
          'enquiries': result.data.enquiries || 0,
          'quick-chat': result.data.quickChat || 0,
          'support-inbox': result.data.supportInbox || 0,
          'live-chat': result.data.liveChat || 0,
          'consultations': result.data.consultations || 0
        })
      }
    } catch (error) {
      console.error('Error loading tab notifications:', error)
    }
  }

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_stats' })
      })
      const result = await response.json()
      if (response.ok && result.data) {
        setStats(result.data)
      }
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }

  const loadRecentTickets = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_recent_tickets' })
      })
      const result = await response.json()
      if (response.ok) setRecentTickets(result.data || [])
    } catch (error) {
      console.error('Error loading recent tickets:', error)
    }
  }

  const loadCurrentAgent = async () => {
    try {
      if (!currentUser?.email) return
      const response = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_current_agent', data: { email: currentUser.email } })
      })
      const result = await response.json()
      if (response.ok && result.data) setCurrentAgent(result.data)
    } catch (error) {
      console.error('Error loading current agent:', error)
    }
  }

  const __getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800'
      case 'in_progress': return 'bg-yellow-100 text-yellow-800'
      case 'waiting_user': return 'bg-orange-100 text-orange-800'
      case 'resolved': return 'bg-green-100 text-green-800'
      case 'closed': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const __formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const tabs = [
    { id: 'quick-chat', name: 'Quick Chat', icon: '💬' },
    { id: 'support-inbox', name: 'Support Inbox', icon: '📥' },
    { id: 'live-chat', name: 'Live Chat', icon: '🔴' },
    { id: 'consultations', name: 'Consultations', icon: '📅' },
    { id: 'agents', name: 'Agent Management', icon: '👥' },
    { id: 'canned-responses', name: 'Canned Responses', icon: '📝' },
    { id: 'automations', name: 'Automations', icon: '⚙️' },
    { id: 'sla', name: 'SLA Policies', icon: '⏱️' },
    { id: 'analytics', name: 'Analytics', icon: '📈' }
  ]

  if (loading && activeTab === 'support-inbox') {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Support Dashboard</h2>
          <p className="text-gray-600">
            Comprehensive support management and analytics
          </p>
        </div>
        {currentAgent && (
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">{currentAgent.agent_name || currentAgent.name}</div>
              <div className="text-sm text-gray-500">Support Agent</div>
            </div>
            {(() => {
              const avail = Array.isArray(currentAgent.availability) ? currentAgent.availability[0] : currentAgent.availability
              const status = avail?.status
              return (
                <div className={`w-3 h-3 rounded-full ${
                  status === 'available' ? 'bg-green-500' :
                  status === 'busy' ? 'bg-yellow-500' :
                  status === 'away' ? 'bg-orange-500' :
                  'bg-gray-500'
                }`}></div>
              )
            })()}
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const count = tabNotifications[tab.id] || 0
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-yellow-500 text-yellow-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
                {count > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-xs font-bold text-white">
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Content */}
      <div>
        {activeTab === 'quick-chat' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Chat Messages</h3>
            <p className="text-gray-600 mb-4">View and respond to user quick chat messages.</p>
            <QuickChatAdmin adminUser={currentUser} />
          </div>
        )}

        {activeTab === 'support-inbox' && (
          <div>
            <SupportInbox adminUser={currentUser} />
          </div>
        )}

        {activeTab === 'live-chat' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Chat Management</h3>
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <p className="text-gray-600">Live chat sessions will appear here when agents are online.</p>
            </div>
          </div>
        )}

        {activeTab === 'consultations' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Consultation Bookings</h3>
            <p className="text-gray-600 mb-4">Manage and approve private consultation requests.</p>
            <ConsultationAdmin adminUser={currentUser} />
          </div>
        )}

        {activeTab === 'agents' && (
          <div>
            <SupportAgentManager currentUser={currentUser} />
          </div>
        )}

        {activeTab === 'canned-responses' && (
          <div>
            <CannedResponseManager />
          </div>
        )}

        {activeTab === 'automations' && (
          <div>
            <TicketAutomations />
          </div>
        )}

        {activeTab === 'sla' && (
          <div>
            <SLAPolicyManager />
          </div>
        )}

        {activeTab === 'analytics' && (
          <div>
            <SupportAnalytics />
          </div>
        )}
      </div>
    </div>
  )
}

export default ComprehensiveSupportDashboard
